import { useCallback } from 'react';
import { ScenarioDefaults } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario.type';
import { FishGroupSummary } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-result.type';
import { PostProcessRequestInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-post-process-input.type';
import { PostProcessUpdateInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/update-scenario-post-process-input.type';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';

export type Override = PostProcessUpdateInput & {
  fishGroupId?: string;
  useScenarioDefaults: boolean;
};

export const usePostProcessMapper = () => {
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();
  const { financialData, cageData, globalSiteId, scenarioDefaults } = scenario ?? {};

  // Builds the initial post-process request body using scenario defaults, with no overrides applied.
  const buildPostProcessBody = useCallback((): PostProcessRequestInput | null => {
    if (!cageData || !globalSiteId || !scenarioDefaults || !financialData) return null;

    return {
      cageData,
      financialData,
      globalSiteId,
      scenarioDefaults,
      useScenarioDefaults: true,
    };
  }, [scenario]);

  // Builds a post-process request body applying the given override to the targeted fish group (or all cages when useScenarioDefaults is true).
  // Non-targeted cages fall back to their current values from fishGroupSummaries, or activeDefaults if not found.
  // Pass activeDefaults (postProcessData.scenarioDefaults) to ensure the fallback reflects the current active state, not the original scenario.
  const buildPostProcessOverride = useCallback(
    (
      override: Override,
      fishGroupSummaries: FishGroupSummary[] | undefined,
      activeDefaults?: ScenarioDefaults
    ): PostProcessRequestInput | null => {
      if (!cageData || !globalSiteId || !scenarioDefaults || !financialData) return null;

      const fishGroupDefaults = activeDefaults ?? scenarioDefaults;

      const overrideValues = {
        harvestTarget: override.harvestTarget,
        harvestTargetType: override.harvestTargetType,
        mortalityProfileId: override.mortalityProfileId,
        mortalityProfileName: override.mortalityProfileName,
      };

      const resolveCageData = (cage: (typeof cageData)[number]) => {
        if (override.useScenarioDefaults) {
          return { fishGroupId: cage.fishGroupId, ...overrideValues };
        }

        const current = fishGroupSummaries?.find((fg) => fg.fishGroupId === cage.fishGroupId);
        const harvestTargetType = current?.harvestTargetType ?? fishGroupDefaults.harvestTargetType;

        if (cage.fishGroupId === override.fishGroupId) {
          return {
            fishGroupId: cage.fishGroupId,
            harvestTarget: override.harvestTarget,
            harvestTargetType,
            mortalityProfileId: override.mortalityProfileId,
            mortalityProfileName: override.mortalityProfileName,
          };
        }

        return {
          fishGroupId: cage.fishGroupId,
          harvestTarget: current?.harvestTarget ?? fishGroupDefaults.harvestTarget,
          harvestTargetType,
          mortalityProfileId: current?.mortalityProfileId ?? fishGroupDefaults.mortalityProfileId,
        };
      };

      return {
        globalSiteId,
        financialData,
        useScenarioDefaults: override.useScenarioDefaults,
        scenarioDefaults: override.useScenarioDefaults ? overrideValues : fishGroupDefaults,
        cageData: cageData.map(resolveCageData),
      };
    },
    [scenario]
  );

  // Builds a reset request body using the provided site-level defaults.
  // Pass a fishGroupId to reset only that cage; omit it to reset all cages to site defaults.
  const resetToSiteLevel = useCallback(
    (
      fishGroupSummaries: FishGroupSummary[] | undefined,
      defaults: ScenarioDefaults | undefined,
      fishGroupId?: string
    ): PostProcessRequestInput | null => {
      if (!defaults) return null;

      return buildPostProcessOverride(
        {
          useScenarioDefaults: !fishGroupId,
          fishGroupId,
          harvestTarget: defaults.harvestTarget,
          harvestTargetType: defaults.harvestTargetType,
          mortalityProfileId: defaults.mortalityProfileId,
        },
        fishGroupSummaries,
        defaults
      );
    },
    [buildPostProcessOverride]
  );

  return { buildPostProcessBody, buildPostProcessOverride, resetToSiteLevel };
};
